import { Application, Asset, Texture, Quat, ADDRESS_CLAMP_TO_EDGE, FILTER_LINEAR, PIXELFORMAT_R8_G8_B8_A8, PIXELFORMAT_RGB8, PIXELFORMAT_RGBA8 } from 'playcanvas';
import type { SceneCubemapFaces, SceneSkyboxConfig } from '../config/manifest';

type CubemapSkyboxConfig = Extract<SceneSkyboxConfig, { type: 'cubemap' }>;
type VideoSkyboxConfig = Extract<SceneSkyboxConfig, { type: 'video' }>;

interface CubemapFaceSelection {
    faces: SceneCubemapFaces;
    variant: string;
}

function nowMs(): number {
    return typeof performance !== 'undefined' ? performance.now() : Date.now();
}

function toCubemapTextureUrls(faces: SceneCubemapFaces): string[] {
    return [faces.px, faces.nx, faces.py, faces.ny, faces.pz, faces.nz];
}

/** 面名（索引序）：cubemap 六面 = CUBE_MAP_POSITIVE_X 起的 px,nx,py,ny,pz,nz。 */
const CUBEMAP_FACE_NAMES = ['px', 'nx', 'py', 'ny', 'pz', 'nz'] as const;

/**
 * 优先级档间的启动错峰间隔：只拉开六面下载的启动次序（前面的面先占带宽、
 * 先建组 cube），不拖总时长——末面也只晚 1s，相对单面下载耗时可忽略。
 */
const FACE_LOAD_STAGGER_MS = 200;

/**
 * 由「玩家出生朝向 ψ（spawnAngles[1]）+ 天空盒旋转 θ（rotationYaw）」折算六面
 * 加载优先级，返回按 前→后→左→右→上→下 排序的面索引数组。
 *
 * 推导（引擎源码级核对）：skybox 片元着色器采样向量 s = envDir 左乘
 * cubeMapRotationMatrix（uniform 值 = invertMat4(TRS(q_θ))，GLSL 行向量×矩阵
 * 数学上等价 R_y(θ)·v），随后 X 分量取反。相机 yaw 语义与 camera-utils 一致：
 * 前向 = R_y(ψ)·(0,0,-1)（yaw0 朝 -Z、右转减 yaw）。两次绕 Y 旋转可合成，水平
 * 基向量的采样结果只依赖 φ=(θ+ψ) mod 360：
 *   前(0,-1) → s=(sinφ,-cosφ)：|cosφ|≥|sinφ| 时 Z 轴面（cosφ≥0=nz 否则 pz），
 *     否则 X 轴面（sinφ≥0=px 否则 nx）
 *   右(1,0) → s=(-cosφ,-sinφ)：|cosφ|≥|sinφ| 时 X 轴面（cosφ≥0=nx 否则 px），
 *     否则 Z 轴面（sinφ≥0=nz 否则 pz）
 *   后/左 = 前/右的对面（面索引 ^1）；上=py、下=ny 恒定（绕 Y 旋转不混垂直面）。
 * 四个水平面恒互异（前与右必分属不同轴），去重仅作 45° 平角的后备保险。
 * 内容锚点实测：laomendong2（θ=98,ψ=270.8→φ=8.8°）前=nz——mobile nz 322KB
 * 是街道纵深主视图，与出生视线一致；meihuashan（θ=226,ψ=181→φ=47°）前=px。
 */
function cubemapFacePriority(rotationYaw: number | undefined, viewYawDeg: number | undefined): number[] {
    const phi = (normalizeSkyboxRotationYaw(rotationYaw) + normalizeSkyboxRotationYaw(viewYawDeg)) % 360;
    const sin = Math.sin(phi * Math.PI / 180);
    const cos = Math.cos(phi * Math.PI / 180);
    const zDominant = Math.abs(cos) >= Math.abs(sin);
    const front = zDominant ? (cos >= 0 ? 5 : 4) : (sin >= 0 ? 0 : 1);
    const right = zDominant ? (cos >= 0 ? 1 : 0) : (sin >= 0 ? 5 : 4);
    const ranked: number[] = [];
    const seen = new Set<number>();
    for (const face of [front, front ^ 1, right ^ 1, right, 2, 3]) {
        if (seen.has(face)) continue;
        seen.add(face);
        ranked.push(face);
    }
    for (let face = 0; face < 6; face++) {
        if (!seen.has(face)) ranked.push(face);
    }
    return ranked;
}

const _skyboxRotation = new Quat();

function normalizeSkyboxRotationYaw(rotationYaw: number | undefined): number {
    if (!Number.isFinite(rotationYaw)) return 0;
    return ((((rotationYaw as number) % 360) + 360) % 360);
}

function applySkyboxRotation(app: Application, rotationYaw: number | undefined): void {
    _skyboxRotation.setFromEulerAngles(0, normalizeSkyboxRotationYaw(rotationYaw), 0);
    app.scene.skyboxRotation = _skyboxRotation;
}

function selectCubemapFaces(config: CubemapSkyboxConfig): CubemapFaceSelection {
    return { faces: config.faces, variant: `yaw-${normalizeSkyboxRotationYaw(config.rotationYaw)}` };
}

function isUnsupportedDdsFallback(url: string, texture: Texture): boolean {
    const info = texture as Texture & { name?: string; width?: number; height?: number };
    return /\.dds$/i.test(url) &&
        info.name === 'dds-legacy-empty' &&
        info.width === 4 &&
        info.height === 4;
}

function formatTextureSize(texture: Texture | null | undefined): string {
    return texture instanceof Texture ? `${texture.width}x${texture.height}` : 'null';
}

class SkyboxSystem {
    private _app: Application;
    private _asset: Asset | null = null;
    private _videoElement: HTMLVideoElement | null = null;
    private _videoTexture: Texture | null = null;
    private _videoRafId: number | null = null;
    /** cubemap 逐面加载在途的面纹理资产（含已上屏的——源位图被 cube 引用着）。 */
    private _cubemapFaceAssets: Asset[] = [];
    /** 逐面合成的 cube 纹理（视觉天空盒本体，scene.skybox 指向它）。 */
    private _cubemapCube: Texture | null = null;
    /** cubemap 载入代数：每次 load/destroy 自增，旧代回调一律不落地。 */
    private _cubemapEpoch = 0;
    /** 最近一次应用天空盒时用过的 yaw（texture/video 恒 0）：换装失败回滚用。 */
    private _lastAppliedRotationYaw = 0;

    constructor(app: Application) {
        this._app = app;
    }

    /**
     * 加载并应用天空盒。
     *
     * @param options.keepCurrent 换装模式：不清当前天空盒——下载期间旧图继续
     *   显示，新图成功应用后才释放旧资产；失败/中途销毁则保留现状（不闪
     *   清屏色）。桌面端「手机档先顶上、全尺寸后台换装」用它。注意 video
     *   型新图的应用时机在 loadeddata 异步回调，走此模式时旧资产不做即时
     *   释放（留到下次 load/destroy，生产场景均为 cubemap）。
     *
     * @param options.viewYawDeg 玩家出生朝向（manifest.spawnAngles[1]）：
     *   cubemap 六面按 前→后→左→右→上→下 的优先级错峰加载（见
     *   cubemapFacePriority），首面建组时整个天空盒以「前」面镜像顶位——
     *   进场第一眼即是正确朝向的街景，而非任意先到的面。
     */
    async load(config?: SceneSkyboxConfig, options?: { keepCurrent?: boolean; viewYawDeg?: number }): Promise<void> {
        const previousAsset = this._asset;
        // 换装模式才需要保留旧 cubemap 状态供回滚/成功后释放；非换装路径
        // destroy() 已即时卸载，无需重复释放。
        const previousCubemap = options?.keepCurrent
            ? { faceAssets: this._cubemapFaceAssets.slice(), cube: this._cubemapCube }
            : null;
        const rollbackYaw = this._lastAppliedRotationYaw;
        const rollbackIntensity = this._app.scene.skyboxIntensity;
        if (!options?.keepCurrent) {
            this.destroy();
            if (!config) return;
        } else if (!config) {
            return; // 无新配置：保持现状
        }

        if (config.type === 'cubemap') {
            await this.loadCubemapSkybox(config, { previousCubemap, rollbackYaw, rollbackIntensity, viewYawDeg: options?.viewYawDeg });
            return;
        }
        if (config.type === 'video') {
            this.loadVideoSkybox(config);
            return;
        }
        await this.loadTextureSkybox(config);
        this.releaseSupersededAsset(previousAsset);
    }

    /** 换装成功后释放被顶掉的旧资产（scene 已不引用其纹理）。 */
    private releaseSupersededAsset(asset: Asset | null): void {
        if (!asset || asset === this._asset) return;
        this._app.assets.remove(asset);
        asset.unload();
    }

    private async loadTextureSkybox(config: Extract<SceneSkyboxConfig, { type: 'texture' }>): Promise<void> {
        const startedAt = nowMs();
        let lastProgressLogMs = Number.NEGATIVE_INFINITY;
        console.info(`[skybox] texture:start url=${config.url}`);
        const asset = new Asset(`skybox:${config.url}`, 'texture', { url: config.url });
        this._asset = asset;
        this._app.assets.add(asset);

        const texture = await new Promise<Texture>((resolve, reject) => {
            asset.on('progress', (received: number, total: number) => {
                const progressAt = nowMs();
                if (progressAt - lastProgressLogMs < 1000) return;
                lastProgressLogMs = progressAt;
                console.info(
                    `[skybox] texture:progress url=${config.url}` +
                    ` received=${Math.round(received ?? 0)} total=${Math.round(total ?? 0)}` +
                    ` elapsedMs=${Math.round(progressAt - startedAt)}`
                );
            });
            asset.once('load', () => {
                console.info(`[skybox] texture:asset-loaded url=${config.url} elapsedMs=${Math.round(nowMs() - startedAt)}`);
                const resource = asset.resource;
                if (resource instanceof Texture) resolve(resource);
                else reject(new Error(`Skybox asset did not load as a texture: ${config.url}`));
            });
            asset.once('error', (err: unknown) => {
                console.warn(`[skybox] texture:error url=${config.url} elapsedMs=${Math.round(nowMs() - startedAt)}`, err);
                reject(new Error(String(err)));
            });
            this._app.assets.load(asset);
        });

        if (this._asset !== asset) return;
        if (isUnsupportedDdsFallback(config.url, texture)) {
            this.destroy();
            throw new Error(`Unsupported DDS skybox texture: ${config.url}. Convert it to a PlayCanvas-supported legacy DDS before using it as a skybox.`);
        }
        this._app.scene.skybox = texture;
        this._app.scene.skyboxIntensity = config.intensity ?? 1;
        this._lastAppliedRotationYaw = 0;
        applySkyboxRotation(this._app, 0);
        console.info(
            `[skybox] texture:applied url=${config.url}` +
            ` size=${texture.width}x${texture.height} totalMs=${Math.round(nowMs() - startedAt)}`
        );
    }

    /**
     * cubemap 逐面渐进加载：六个面各自独立下载，谁先解码谁先上屏。
     *
     * 机制：首个成功的面建组 cube 纹理并设为 scene.skybox——未到货的槽位
     * 暂以该面镜像占位（WebGL 要求六面同尺寸才 cube-complete，混尺寸整盒
     * 变黑，也不能用 1×1 白图占位）；后续每面到货 setSource 换入（引擎按
     * _levelsUpdated 面级脏标走 texSubImage2D 单面重传，其余面不重复上
     * 传）。合成配方与引擎 CubemapHandler.update 的 _faces 组装一致（面序
     * px,nx,py,ny,pz,nz = CUBE_MAP_POSITIVE_X 起的六面）。
     *
     * 失败语义：换装（keepCurrent）模式任一面失败→回滚旧天空盒（低清完整
     * 好过高清带镜像假面）；首装模式部分失败→保留已到货的面（镜像占位顶
     * 缺面）；全部失败→reject（调用方记 failed，场景维持清屏色/旧盒）。
     *
     * 六面按 前→后→左→右→上→下 优先级错峰启动（cubemapFacePriority）：
     * 「前」面（玩家出生视线方向的采样面）最先建组 cube，其镜像顶位期间
     * 天空盒呈现的就是正确朝向的内容。
     */
    private async loadCubemapSkybox(
        config: CubemapSkyboxConfig,
        rollback: { previousCubemap: { faceAssets: Asset[]; cube: Texture | null } | null; rollbackYaw: number; rollbackIntensity: number; viewYawDeg?: number }
    ): Promise<void> {
        const startedAt = nowMs();
        const selection = selectCubemapFaces(config);
        const urls = toCubemapTextureUrls(selection.faces);
        const priority = cubemapFacePriority(config.rotationYaw, rollback.viewYawDeg);
        console.info(
            `[skybox] cubemap:start variant=${selection.variant} faces=${urls.length} urls=${urls.join(',')}` +
            ` progressive=per-face priority=${priority.map((f) => CUBEMAP_FACE_NAMES[f]).join('>')}`
        );

        const epoch = ++this._cubemapEpoch;
        this._cubemapFaceAssets = [];
        this._cubemapCube = null;
        // 当前 cube 槽位的源数组：必须每次 setSource 传「新数组」——引擎脏检
        // 测比较新旧数组的元素，原地改同一数组再传自身会恒等→永不标脏。
        let liveSlots: unknown[] = [];
        // 合成进度走局部变量（this._cubemapCube 的闭包赋值会让 TS 流分析把
        // 后续读取窄化成 null），字段只作 destroy/回滚的全局记账。
        let composedCube: Texture | null = null;
        let firstFaceTexture: Texture | null = null;
        let successes = 0;
        let failures = 0;

        const failFace = (index: number, url: string, reason: string): void => {
            failures++;
            console.warn(`[skybox] cubemap:face-error idx=${index} url=${url} elapsedMs=${Math.round(nowMs() - startedAt)} reason=${reason}`);
        };

        const applyFace = (index: number, faceTexture: Texture): void => {
            successes++;
            const source = faceTexture._levels?.[0];
            if (composedCube) {
                if (faceTexture.width !== composedCube.width || faceTexture.height !== composedCube.height) {
                    // 尺寸不一致的面不能换入（cube-complete 约束），按失败处理。
                    successes--;
                    failFace(index, urls[index], `face size ${faceTexture.width}x${faceTexture.height} != cube ${composedCube.width}x${composedCube.height}`);
                    return;
                }
                const next = liveSlots.slice();
                next[index] = source;
                // 类型联合里没有 ImageBitmap（运行时支持），按引擎自身用法透传。
                composedCube.setSource(next as unknown as HTMLImageElement[], 0);
                liveSlots = next;
                console.info(`[skybox] cubemap:face idx=${index} size=${formatTextureSize(faceTexture)} ok=${successes}/6 elapsedMs=${Math.round(nowMs() - startedAt)}`);
                return;
            }
            // 首面：建组 cube，未到货槽位以本面镜像占位。
            firstFaceTexture = faceTexture;
            liveSlots = [source, source, source, source, source, source];
            const cube = new Texture(this._app.graphicsDevice, {
                name: `skybox:${selection.variant}`,
                cubemap: true,
                width: faceTexture.width,
                height: faceTexture.height,
                format: faceTexture.format === PIXELFORMAT_RGB8 ? PIXELFORMAT_RGBA8 : faceTexture.format,
                type: faceTexture.type,
                mipmaps: false,
                // 选项类型联合只声明了原始数据形态的 per-level 数组（Uint8Array[][]），
                // ImageBitmap 面源运行时同样合法（引擎 _isBrowserInterface 分支）。
                levels: [liveSlots.slice()] as unknown as Uint8Array[][],
                minFilter: faceTexture.minFilter,
                magFilter: faceTexture.magFilter,
                addressU: ADDRESS_CLAMP_TO_EDGE,
                addressV: ADDRESS_CLAMP_TO_EDGE
            });
            composedCube = cube;
            this._cubemapCube = cube;
            this._app.scene.setSkybox([cube]);
            this._app.scene.skyboxIntensity = config.intensity ?? 1;
            this._lastAppliedRotationYaw = normalizeSkyboxRotationYaw(config.rotationYaw);
            applySkyboxRotation(this._app, config.rotationYaw);
            console.info(
                `[skybox] cubemap:composed size=${faceTexture.width}x${faceTexture.height}` +
                ` firstFace=${index} pending=${5} elapsedMs=${Math.round(nowMs() - startedAt)}`
            );
        };

        const faces = urls.map((url, index) => new Promise<void>((resolve) => {
            const asset = new Asset(`skybox:face${index}:${url}`, 'texture', { url });
            this._cubemapFaceAssets.push(asset);
            this._app.assets.add(asset);
            asset.once('load', () => {
                if (epoch !== this._cubemapEpoch) return resolve();
                const texture = asset.resource;
                if (!(texture instanceof Texture) || isUnsupportedDdsFallback(url, texture)) {
                    failFace(index, url, texture instanceof Texture ? 'unsupported legacy dds' : 'not a texture');
                } else {
                    applyFace(index, texture);
                }
                resolve();
            });
            asset.once('error', (err: unknown) => {
                if (epoch !== this._cubemapEpoch) return resolve();
                failFace(index, url, String(err));
                resolve();
            });
            // 错峰启动：rank 0（前）立即下载，其余按优先级档位各延一档。等待期
            // 间被 destroy/新 load 接管（epoch 变化）则不发请求、直接 resolve，
            // Promise.all 不悬挂（旧代资产已被 destroy/换装路径回收）。
            const rank = priority.indexOf(index);
            const startLoad = () => {
                if (epoch !== this._cubemapEpoch) return resolve();
                this._app.assets.load(asset);
            };
            if (rank <= 0) startLoad();
            else window.setTimeout(startLoad, rank * FACE_LOAD_STAGGER_MS);
        }));
        await Promise.all(faces);

        if (epoch !== this._cubemapEpoch) return; // 期间被 destroy/新 load 接管，不动场景

        if (successes === 0) {
            // 全灭：首装无天空盒（清屏色），换装旧盒未动过——reject 维持调用
            // 方的 failed 日志语义。换装路径清掉本次失败的面资产、字段归还
            // 旧状态，避免旧面资产脱管泄漏。
            if (rollback.previousCubemap) {
                for (const asset of this._cubemapFaceAssets) {
                    this._app.assets.remove(asset);
                    asset.unload();
                }
                this._cubemapFaceAssets = rollback.previousCubemap.faceAssets;
                this._cubemapCube = rollback.previousCubemap.cube;
            }
            throw new Error(`Skybox cubemap asset did not load: ${Object.values(selection.faces).join('|')}`);
        }
        if (failures > 0 && rollback.previousCubemap) {
            // 换装中途有面失败：回滚旧天空盒（含强度/旋转），放弃部分升级。
            console.warn(`[skybox] cubemap:rollback variant=${selection.variant} ok=${successes} fail=${failures} — keep previous skybox`);
            const previous = rollback.previousCubemap;
            this._destroyCurrentCubemapCube();
            for (const asset of this._cubemapFaceAssets) {
                this._app.assets.remove(asset);
                asset.unload();
            }
            this._cubemapFaceAssets = previous.faceAssets;
            this._cubemapCube = previous.cube;
            this._app.scene.skybox = previous.cube;
            this._app.scene.skyboxIntensity = rollback.rollbackIntensity;
            this._lastAppliedRotationYaw = rollback.rollbackYaw;
            applySkyboxRotation(this._app, rollback.rollbackYaw);
            return;
        }
        if (failures > 0) {
            // 首装部分成功：保留（缺面为镜像占位），不 reject。
            console.warn(`[skybox] cubemap:partial variant=${selection.variant} ok=${successes} fail=${failures}`);
        }
        console.info(
            `[skybox] cubemap:applied variant=${selection.variant} faces=${successes}/${urls.length}` +
            ` size=${formatTextureSize(firstFaceTexture)}` +
            ` totalMs=${Math.round(nowMs() - startedAt)}`
        );
        // 换装成功：释放被顶掉的旧 cubemap（面资产 + 旧 cube）。
        if (rollback.previousCubemap) this._releaseSupersededCubemap(rollback.previousCubemap);
    }

    /** 销毁当前合成 cube（独立方法：字段在闭包里的赋值不影响此处的窄化）。 */
    private _destroyCurrentCubemapCube(): void {
        if (this._cubemapCube) {
            this._cubemapCube.destroy();
            this._cubemapCube = null;
        }
    }

    /** 换装成功后释放被顶掉的旧 cubemap 状态（旧 cube 不再被场景引用时）。 */
    private _releaseSupersededCubemap(previous: { faceAssets: Asset[]; cube: Texture | null }): void {
        if (previous.cube && previous.cube !== this._cubemapCube && this._app.scene.skybox !== previous.cube) {
            previous.cube.destroy();
        }
        for (const asset of previous.faceAssets) {
            this._app.assets.remove(asset);
            asset.unload();
        }
    }

    /**
     * Load a panoramic (equirect 360°) video as the skybox.  Creates a
     * hidden <video> element, a PlayCanvas Texture backed by it, and
     * continuously uploads video frames to the texture via rAF.  The
     * texture is assigned to scene.skybox (single-texture skybox mode).
     */
    private loadVideoSkybox(config: VideoSkyboxConfig): void {
        const video = document.createElement('video');
        video.src = config.url;
        video.muted = true;
        video.loop = config.loop ?? true;
        video.playsInline = true;
        video.crossOrigin = 'anonymous';
        video.autoplay = config.autoplay ?? true;
        video.style.display = 'none';
        document.body.appendChild(video);
        this._videoElement = video;

        const texture = new Texture(this._app.graphicsDevice, {
            name: `skybox-video:${config.url}`,
            width: 2,
            height: 1,
            format: PIXELFORMAT_R8_G8_B8_A8,
            mipmaps: false,
            minFilter: FILTER_LINEAR,
            magFilter: FILTER_LINEAR,
            addressU: ADDRESS_CLAMP_TO_EDGE,
            addressV: ADDRESS_CLAMP_TO_EDGE
        });
        this._videoTexture = texture;

        const applyTexture = () => {
            texture.setSource(video);
            this._app.scene.skybox = texture;
            this._app.scene.skyboxIntensity = config.intensity ?? 1;
            this._lastAppliedRotationYaw = 0;
            applySkyboxRotation(this._app, 0);
            console.info(`[skybox] video:applied url=${config.url}`);
        };

        const pump = () => {
            if (!this._videoTexture) return;
            texture.setSource(video);
            texture.upload();
            this._videoRafId = requestAnimationFrame(pump);
        };

        video.addEventListener('loadeddata', () => {
            applyTexture();
            pump();
        }, { once: true });

        video.addEventListener('error', (e) => {
            console.warn(`[skybox] video:error url=${config.url}`, e);
        });

        // Attempt to start playback; muted autoplay is generally allowed.
        video.play().catch((err) => {
            console.warn(`[skybox] video:playback blocked url=${config.url}`, err);
        });
    }

    destroy(): void {
        this._cubemapEpoch++; // 在途回调一律不落地
        for (const asset of this._cubemapFaceAssets) {
            this._app.assets.remove(asset);
            asset.unload();
        }
        this._cubemapFaceAssets = [];
        if (this._cubemapCube) {
            this._cubemapCube.destroy();
            this._cubemapCube = null;
        }
        if (this._videoRafId !== null) {
            cancelAnimationFrame(this._videoRafId);
            this._videoRafId = null;
        }
        if (this._videoTexture) {
            this._videoTexture.destroy();
            this._videoTexture = null;
        }
        if (this._videoElement) {
            this._videoElement.pause();
            this._videoElement.remove();
            this._videoElement = null;
        }
        this._app.scene.setSkybox();
        this._app.scene.skybox = null;
        applySkyboxRotation(this._app, 0);
        if (this._asset) {
            this._app.assets.remove(this._asset);
            this._asset.unload();
            this._asset = null;
        }
    }
}

export { SkyboxSystem };
